import {
  VendorPricingAgreement,
  ApiVendorPricingAgreement,
  apiVendorPricingAgreementToVendorPricingAgreement,
  SearchVendorPricingAgreementsParams,
  VendorPricingAgreementScopes,
} from '@types'

interface GetUniversalVendorPricingAgreementsCoordinator {
  RestService: {
    get: (path: string) => Promise<ApiVendorPricingAgreement[] | null>
  }
  VendorPricingAgreementsService: {
    setUniversalVendorPricingAgreements: (
      vendorPricingAgreement: VendorPricingAgreement[] | null,
    ) => void
  }
  HandleError: any
}
export const GetUniversalVendorPricingAgreements =
  ({
    RestService,
    VendorPricingAgreementsService,
    HandleError,
  }: GetUniversalVendorPricingAgreementsCoordinator) =>
  async () => {
    try {
      const data: ApiVendorPricingAgreement[] | null = await RestService.get(
        '/chefs/vendor-pricing-agreements/universal',
      )

      const vendorPricingAgreements = data
        ? data.map(apiVendorPricingAgreementToVendorPricingAgreement)
        : null
      VendorPricingAgreementsService.setUniversalVendorPricingAgreements(
        vendorPricingAgreements,
      )

      return vendorPricingAgreements
    } catch (error) {
      HandleError({ error })
      VendorPricingAgreementsService.setUniversalVendorPricingAgreements(null)

      return null
    }
  }

interface GetMarketVendorPricingAgreementCoordinator {
  RestService: {
    get(
      path: string,
      params: { hqId: number },
    ): Promise<ApiVendorPricingAgreement[] | null>
  }
  VendorPricingAgreementsService: {
    setMarketVendorPricingAgreements: (
      vendorPricingAgreements: VendorPricingAgreement[] | null,
    ) => void
  }
  HandleError: any
}
export const GetMarketVendorPricingAgreements =
  ({
    RestService,
    VendorPricingAgreementsService,
    HandleError,
  }: GetMarketVendorPricingAgreementCoordinator) =>
  async ({ hqId }: { hqId: number }) => {
    try {
      const data: ApiVendorPricingAgreement[] | null = await RestService.get(
        `/chefs/vendor-pricing-agreements/market`,
        { hqId },
      )

      const vendorPricingAgreements = data
        ? data.map(apiVendorPricingAgreementToVendorPricingAgreement)
        : null
      VendorPricingAgreementsService.setMarketVendorPricingAgreements(
        vendorPricingAgreements,
      )

      return vendorPricingAgreements
    } catch (error) {
      HandleError({ error })
      VendorPricingAgreementsService.setMarketVendorPricingAgreements(null)

      return null
    }
  }

interface GetVendorPricingAgreementsCoordinator {
  RestService: {
    get(
      path: string,
      params: { vendorId: string; hqId: number },
    ): Promise<ApiVendorPricingAgreement[] | null>
  }
  VendorPricingAgreementsService: {
    setVendorMarketVendorPricingAgreements: (
      vendorPricingAgreements: VendorPricingAgreement[] | null,
    ) => void
  }
  HandleError: any
}
export const GetVendorPricingAgreements =
  ({
    RestService,
    VendorPricingAgreementsService,
    HandleError,
  }: GetVendorPricingAgreementsCoordinator) =>
  async ({ vendorId, hqId }: { vendorId: string; hqId: number }) => {
    try {
      const data: ApiVendorPricingAgreement[] | null = await RestService.get(
        `/chefs/vendor-pricing-agreements/vendor`,
        {
          vendorId,
          hqId,
        },
      )

      const vendorPricingAgreements = data
        ? data.map(apiVendorPricingAgreementToVendorPricingAgreement)
        : null
      VendorPricingAgreementsService.setVendorMarketVendorPricingAgreements(
        vendorPricingAgreements,
      )

      return vendorPricingAgreements
    } catch (error) {
      HandleError({ error })
      VendorPricingAgreementsService.setVendorMarketVendorPricingAgreements(
        null,
      )

      return null
    }
  }

interface SaveVendorPricingAgreementCoordinator {
  RestService: {
    post: (
      path: string,
      data: VendorPricingAgreement,
    ) => Promise<ApiVendorPricingAgreement>
    put: (
      path: string,
      data: VendorPricingAgreement,
    ) => Promise<ApiVendorPricingAgreement>
  }
  VendorPricingAgreementsService: {
    setUniversalVendorPricingAgreementInAgreements: (
      vendorPricingAgreement: VendorPricingAgreement | null,
    ) => void
    setMarketVendorPricingAgreementInAgreements: (
      vendorPricingAgreement: VendorPricingAgreement | null,
    ) => void
    setVendorMarketVendorPricingAgreementInAgreements: (
      vendorPricingAgreement: VendorPricingAgreement | null,
    ) => void
  }
  HandleError: any
}
export const SaveVendorPricingAgreement =
  ({
    RestService,
    VendorPricingAgreementsService,
    HandleError,
  }: SaveVendorPricingAgreementCoordinator) =>
  async (data: VendorPricingAgreement) => {
    try {
      let result: ApiVendorPricingAgreement
      if (data.id !== '') {
        result = await RestService.put(
          `/chefs/vendor-pricing-agreements/${data.id}`,
          data,
        )
      } else {
        result = await RestService.post(
          '/chefs/vendor-pricing-agreements',
          data,
        )
      }

      const vendorPricingAgreement = result
        ? apiVendorPricingAgreementToVendorPricingAgreement(result)
        : null
      switch (data.scope) {
        case VendorPricingAgreementScopes.UNIVERSAL:
          VendorPricingAgreementsService.setUniversalVendorPricingAgreementInAgreements(
            vendorPricingAgreement,
          )
          break
        case VendorPricingAgreementScopes.MARKET:
          VendorPricingAgreementsService.setMarketVendorPricingAgreementInAgreements(
            vendorPricingAgreement,
          )
          break
        case VendorPricingAgreementScopes.VENDOR_MARKET:
          VendorPricingAgreementsService.setVendorMarketVendorPricingAgreementInAgreements(
            vendorPricingAgreement,
          )
          break
      }

      return vendorPricingAgreement
    } catch (error) {
      HandleError({ error })

      return null
    }
  }

interface SearchVendorPricingAgreementsCoordinator {
  RestService: {
    get(
      path: string,
      params: SearchVendorPricingAgreementsParams,
    ): Promise<ApiVendorPricingAgreement[]>
  }
  HandleError: any
}
export const SearchVendorPricingAgreements =
  ({ RestService, HandleError }: SearchVendorPricingAgreementsCoordinator) =>
  async (params: SearchVendorPricingAgreementsParams) => {
    try {
      const vendorPricingAgreements: ApiVendorPricingAgreement[] =
        await RestService.get(`/chefs/vendor-pricing-agreements/search`, params)

      return vendorPricingAgreements
        ? vendorPricingAgreements.map(
            apiVendorPricingAgreementToVendorPricingAgreement,
          )
        : []
    } catch (error) {
      HandleError({ error })

      return []
    }
  }

interface EditVendorPricingAgreementCoordinator {
  UIService: {
    EditVendorPricingAgreement: {
      show: () => void
      close: () => void
    }
  }
  VendorPricingAgreementsService: {
    setEditVendorPricingAgreement: (
      vendorPricingAgreement: VendorPricingAgreement,
    ) => void
    clearEditVendorPricingAgreement: () => void
  }
}
export const ShowEditVendorPricingAgreement =
  ({
    UIService,
    VendorPricingAgreementsService,
  }: EditVendorPricingAgreementCoordinator) =>
  (vendorPricingAgreement: VendorPricingAgreement) => {
    UIService.EditVendorPricingAgreement.show()
    VendorPricingAgreementsService.setEditVendorPricingAgreement(
      vendorPricingAgreement,
    )
  }
export const CloseEditVendorPricingAgreement =
  ({
    UIService,
    VendorPricingAgreementsService,
  }: EditVendorPricingAgreementCoordinator) =>
  () => {
    UIService.EditVendorPricingAgreement.close()
    VendorPricingAgreementsService.clearEditVendorPricingAgreement()
  }

interface GenerateVendorPricingAgreementsReportCoordinator {
  RestService: {
    post: (
      path: string,
      data: {
        hqIds: number[]
        email: string
      },
    ) => Promise<boolean>
  }
  HandleError: any
}
export const GenerateVendorPricingAgreementsReport =
  ({
    RestService,
    HandleError,
  }: GenerateVendorPricingAgreementsReportCoordinator) =>
  async (data: { hqIds: number[]; email: string }) => {
    try {
      const result = await RestService.post(
        '/chefs/vendor-pricing-agreements/reports',
        data,
      )

      return result
    } catch (error) {
      HandleError({ error })

      return false
    }
  }
